import React, {useCallback, useState} from 'react';
import graphql from 'babel-plugin-relay/macro';
import {useRefetchable} from 'relay-hooks';
import {FrameStoreBuffer} from '../../types';
import {TourPlayer} from '../../components/TourPlayer';
import {TourPreviewMenu} from './TourPreviewMenu';
import {TourPreviewDrawer} from './TourPreviewDrawer';
import {
  TOUR_DRAWER_HEIGHT,
  TOUR_DRAWER_WIDTH,
} from '../../components/TourDrawer';
import {Box, Stack, Typography, useMediaQuery} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {useSearchParam} from 'react-use';
import {TourWebRTC} from './TourPreviewWebRTC';
import {TourUIContextProvider} from '../../contexts/TourUIContext';
import {AGENT_TOUR_CONTAINER_ID} from '../../../../pages/tour/AgentTourPage';
import {TourAgentInfo} from '../../components/TourAgentInfo';
import {AgentTour_tour$key} from '../../../../__generated__/AgentTour_tour.graphql';
import Dialog from '@mui/material/Dialog';
import {TourPageContainer} from './TourPageContainer';

const fragment = graphql`
  fragment AgentTour_tour on Tour
  @refetchable(queryName: "AgentTourTodoRefetchQuery") {
    ...TourPreviewMarkers_tour
    ...TourPlayerCanvas_tour
    thumbnailURL
    title
    listingLink
    description
    agentProfile {
      ...TourAgentInfo_tour
    }
    viewingSessionRequest {
      rtcToken
      signalingToken
      appId
      viewingSessionUserId
      expireTime
    }
  }
`;

interface TourPreviewProps {
  tourRef: AgentTour_tour$key;
  external?: boolean;
}

export function AgentTour({tourRef, external = false}: TourPreviewProps) {
  const {data: tour} = useRefetchable(fragment, tourRef);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const tourCanvasRef = React.useRef<HTMLCanvasElement>(null);

  const disabledControls = useSearchParam('disabledControls');
  const liveViewing = useSearchParam('liveViewing');
  const splitDisableControls = disabledControls?.toLowerCase()?.split(',');
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [buffer, setBuffer] = useState<FrameStoreBuffer>({
    totalImages: 0,
    storeLength: 0,
    fps: 0,
  });
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [fastTravellingStatus, setFastTravellingStatus] = useState<
    'idle' | 'forward' | 'backward'
  >('idle');
  const [isPortrait, setIsPortrait] = useState(false);
  const [tourSize, setTourSize] = useState<{
    width: number;
    height: number;
    aspectRatio: number;
  } | null>(null);

  const handleGetCanvasDimensions = useCallback(
    (width: number, height: number) => {
      if (width > height) {
        setIsPortrait(false);
      } else {
        setIsPortrait(true);
      }

      if (width && height) {
        setTourSize({width, height, aspectRatio: width / height});
      }

      if (
        width == null ||
        width === 0 ||
        height == null ||
        height === 0 ||
        liveViewing
      ) {
        return;
      }
      const containerHeight = containerRef.current?.clientHeight ?? 0;
      const containerWidth = containerRef.current?.clientWidth ?? 0;

      if (
        height > width &&
        containerHeight - height - parseInt(TOUR_DRAWER_HEIGHT) > 150
      ) {
        setDrawerIsOpen(true);
      } else if (
        !isMobile &&
        width > height &&
        containerWidth - width - parseInt(TOUR_DRAWER_WIDTH) > 450
      ) {
        setDrawerIsOpen(true);
      }
    },
    [containerRef, isMobile, liveViewing]
  );

  const onCloseAgentDetails = useCallback(() => {
    setCurrentIndex(0);
  }, []);

  const tourPlayerContent = (
    <Box
      ref={containerRef}
      id={AGENT_TOUR_CONTAINER_ID}
      position="relative"
      width="100%"
      height="100%"
    >
      <TourUIContextProvider>
        <Stack height="100%" justifyContent="space-between">
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="#758089"
            display="grid"
            sx={{placeContent: 'center'}}
          >
            {tour.thumbnailURL && (
              <Box
                component="img"
                src={tour.thumbnailURL}
                alt="thumbnail"
                sx={{
                  width: '120vw',
                  height: '120dvh',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  filter: 'blur(50px)',
                  opacity: 0.9,
                }}
              />
            )}
          </Box>

          <Stack position="relative">
            {!splitDisableControls?.includes('menu') && (
              <TourPreviewMenu
                drawerIsOpen={drawerIsOpen}
                title={tour.title}
                tourId={tour.id}
                external={external}
              />
            )}
          </Stack>

          <TourPlayer
            fastTravellingStatus={fastTravellingStatus}
            tourRef={tour}
            currentIndex={currentIndex}
            displayController={buffer.storeLength > 0}
            drawerIsOpen={drawerIsOpen}
            drawerRef={drawerRef}
            tourCanvasRef={tourCanvasRef}
            maxIndex={buffer.storeLength}
            setBuffer={setBuffer}
            setCurrentIndex={setCurrentIndex}
            getCanvasDimensions={handleGetCanvasDimensions}
            containerRef={containerRef}
            forceHiRes
            fullContainer
          />

          <Stack alignItems="center" px={2}>
            {liveViewing?.toLowerCase() === 'true' && (
              <TourWebRTC
                drawerRef={drawerRef}
                drawerIsOpen={drawerIsOpen}
                tourId={tour.id}
                viewingSessionRequest={tour.viewingSessionRequest}
                isHost={!external}
              />
            )}
            {!splitDisableControls?.includes('markers') && (
              <TourPreviewDrawer
                setFastTravellingStatus={setFastTravellingStatus}
                drawerRef={drawerRef}
                drawerIsOpen={drawerIsOpen}
                keepDrawerOpen={false}
                setDrawerIsOpen={setDrawerIsOpen}
                tourRef={tour}
                buffer={buffer}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                liveViewing={liveViewing?.toLowerCase() === 'true'}
              />
            )}
          </Stack>
        </Stack>
      </TourUIContextProvider>
    </Box>
  );

  const tourInfoContent = (
    <Stack flex={1} spacing={2}>
      <Typography variant="h4" component="h2">
        Tour Info
      </Typography>
      <Typography color="#191C1F" variant="body1">
        {tour.description ?? 'No description provided.'}
      </Typography>
    </Stack>
  );

  const agentInfoContent = tour.agentProfile && (
    <TourAgentInfo
      tourName={tour.title}
      agentRef={tour.agentProfile}
      listingLink={tour.listingLink}
    />
  );

  if (isMobile) {
    return (
      <Stack
        height="100svh"
        maxHeight="100svh"
        overflow="hidden"
        justifyContent="space-between"
      >
        {tourPlayerContent}

        <Dialog
          open={
            currentIndex + 2 >= buffer.totalImages && buffer.totalImages > 0
          }
          fullWidth
          maxWidth="xs"
          PaperProps={{sx: {maxWidth: 300}}}
          onClose={onCloseAgentDetails}
        >
          {tour.agentProfile && (
            <TourAgentInfo
              tourName={tour.title}
              agentRef={tour.agentProfile}
              listingLink={tour.listingLink}
              maxWidth={300}
              onClose={onCloseAgentDetails}
            />
          )}
        </Dialog>
      </Stack>
    );
  }

  return (
    <>
      <TourPageContainer
        isPortrait={isPortrait}
        tourSize={tourSize}
        tourPlayer={tourPlayerContent}
        tourInfo={tourInfoContent}
        agentInfo={agentInfoContent}
      />

      <Dialog
        open={currentIndex + 2 >= buffer.totalImages && buffer.totalImages > 0}
        fullWidth
        maxWidth="xs"
        PaperProps={{sx: {maxWidth: 300}}}
        onClose={onCloseAgentDetails}
      >
        {tour.agentProfile && (
          <TourAgentInfo
            tourName={tour.title}
            agentRef={tour.agentProfile}
            listingLink={tour.listingLink}
            maxWidth={300}
            onClose={onCloseAgentDetails}
          />
        )}
      </Dialog>
    </>
  );
}
